import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/contexts/AuthContext";
import { Loader2, CheckCircle2, XCircle, Clock, Ban, Mail } from "lucide-react";

/**
 * Phase 5C.1b — /accept-invite?token=... page.
 *
 * Preconditions:
 *  - authenticated session (unauthenticated users are bounced to
 *    /login with the token preserved so they come back here).
 *
 * UI states (one at a time; no sensitive token or internal
 * authorization data is ever rendered — only user-friendly copy):
 *  - loading          : server call in flight
 *  - missing_token    : URL has no token at all
 *  - success          : server accepted — new workspace is available
 *  - invalid          : token not recognized
 *  - consumed         : already accepted (replay)
 *  - expired          : past INVITE_TTL_DAYS
 *  - revoked          : revoked/suspended before acceptance
 *  - email_mismatch   : authenticated identity ≠ invite email
 *  - error            : unexpected server failure
 */

const STATE_CODE_MAP = {
  INVITE_INVALID: "invalid",
  INVITE_CONSUMED: "consumed",
  INVITE_EXPIRED: "expired",
  INVITE_REVOKED: "revoked",
  INVITE_EMAIL_MISMATCH: "email_mismatch",
  INVITE_SELF: "invalid",
};

// Module-level in-flight / result cache. React 18 StrictMode mounts
// each effect twice in dev; this cache guarantees we only POST to
// /auth/accept-invite once per token per browser load, and the second
// mount receives the same resolution as the first.
const INVITE_CACHE = new Map(); // token -> Promise<{status, data?, detail?}>

function statusToKind(status, detail) {
  // Backend returns {detail: {code, message}} on these flows.
  const code =
    detail && typeof detail === "object" && typeof detail.code === "string"
      ? detail.code
      : null;
  if (code && STATE_CODE_MAP[code]) return STATE_CODE_MAP[code];
  if (status === 400) return "invalid";
  if (status === 403) return "email_mismatch";
  if (status === 409) return "consumed";
  return "error";
}

export default function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { user, loading, refresh, fetchMemberships, switchWorkspace } =
    useAuth();

  const [kind, setKind] = useState("loading");
  const [accepted, setAccepted] = useState(null); // {workspace_owner, role, level}
  // Guard: StrictMode double-mount + user-state refresh must not post
  // the invite twice. Module-level set survives both.
  const submittedRef = useRef(false);

  useEffect(() => {
    if (loading) return;
    // Unauthenticated users: bounce to login and preserve the token so
    // they land right back here after signing in.
    if (!user || user === false) {
      navigate(`/login?return=${encodeURIComponent(
        `/accept-invite${token ? `?token=${encodeURIComponent(token)}` : ""}`
      )}`);
      return;
    }
    if (!token) {
      setKind("missing_token");
      return;
    }
    if (submittedRef.current) return;
    submittedRef.current = true;
    (async () => {
      try {
        let p = INVITE_CACHE.get(token);
        if (!p) {
          p = axios
            .post(`${API}/auth/accept-invite`, { invite_token: token })
            .then((res) => ({ ok: true, data: res.data }))
            .catch((err) => ({
              ok: false,
              status: err?.response?.status,
              detail: err?.response?.data?.detail,
            }));
          INVITE_CACHE.set(token, p);
        }
        const result = await p;
        if (result.ok) {
          setAccepted(result.data || null);
          setKind("success");
          try {
            await refresh();
            await fetchMemberships();
          } catch {}
        } else {
          setKind(statusToKind(result.status, result.detail));
        }
      } catch (e) {
        setKind("error");
      }
    })();
    // Intentionally no cleanup — we rely on INVITE_CACHE + the
    // submittedRef guard. React StrictMode in dev remounts the
    // component synchronously; using a `cancelled` flag here races
    // against the user-state refresh and leaves the UI stuck.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user, token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F7F9FC] px-4">
      <div
        className="w-full max-w-md bg-white rounded-xl border border-[#eee] p-8 shadow-sm"
        data-testid="accept-invite-page"
      >
        <h1
          className="text-[22px] font-semibold tracking-tight text-black mb-1"
          data-testid="accept-invite-title"
        >
          Accept workspace invite
        </h1>
        <p className="text-[13px] text-[#666] mb-6">
          Zynthoro workspace access
        </p>

        {kind === "loading" && (
          <div
            className="flex items-center gap-3 text-[14px] text-[#333]"
            data-testid="accept-invite-loading"
          >
            <Loader2 className="animate-spin" size={18} /> Verifying your
            invite…
          </div>
        )}

        {kind === "missing_token" && (
          <Message
            testid="accept-invite-missing-token"
            icon={<XCircle size={28} className="text-[#C44]" />}
            title="No invite token"
            body="This invite link is incomplete. Please reopen the invite email and try again."
          />
        )}

        {kind === "success" && (
          <div data-testid="accept-invite-success">
            <Message
              icon={<CheckCircle2 size={28} className="text-[#1A4FFF]" />}
              title="You're in."
              body="You now have access to this workspace. Switch to it anytime from your profile menu."
            />
            <div className="mt-6 flex gap-3">
              <button
                data-testid="accept-invite-go-to-workspace"
                className="flex-1 h-10 rounded-md bg-[#1A4FFF] text-white text-[13px] font-semibold hover:bg-[#0e3fe6]"
                onClick={async () => {
                  if (accepted?.workspace_owner) {
                    try {
                      await switchWorkspace(accepted.workspace_owner);
                      return; // hard-reloads to /dashboard
                    } catch {}
                  }
                  navigate("/dashboard");
                }}
              >
                Enter workspace
              </button>
              <Link
                to="/dashboard"
                data-testid="accept-invite-dashboard-link"
                className="flex-1 h-10 inline-flex items-center justify-center rounded-md border border-[#E5E8F0] text-[13px] font-semibold text-black hover:bg-[#F4F6FB]"
              >
                Dashboard
              </Link>
            </div>
          </div>
        )}

        {kind === "invalid" && (
          <Message
            testid="accept-invite-invalid"
            icon={<XCircle size={28} className="text-[#C44]" />}
            title="Invite not recognized"
            body="This invite link isn't valid. Ask the workspace owner to send you a new one."
          />
        )}

        {kind === "consumed" && (
          <Message
            testid="accept-invite-consumed"
            icon={<CheckCircle2 size={28} className="text-[#6c757d]" />}
            title="Invite already accepted"
            body="This invite has already been used. Open the workspace from your profile menu."
            footerLink
          />
        )}

        {kind === "expired" && (
          <Message
            testid="accept-invite-expired"
            icon={<Clock size={28} className="text-[#C44]" />}
            title="Invite expired"
            body="This invite is older than 14 days. Ask the workspace owner to send a fresh invite."
          />
        )}

        {kind === "revoked" && (
          <Message
            testid="accept-invite-revoked"
            icon={<Ban size={28} className="text-[#C44]" />}
            title="Invite no longer valid"
            body="Access to this workspace was revoked. Contact the workspace owner if you believe this is a mistake."
          />
        )}

        {kind === "email_mismatch" && (
          <Message
            testid="accept-invite-email-mismatch"
            icon={<Mail size={28} className="text-[#C44]" />}
            title="Wrong account"
            body={
              <>
                This invite was sent to a different email address. You're
                signed in as <strong>{user?.email}</strong>. Sign in with the
                invited email to accept.
              </>
            }
          />
        )}

        {kind === "error" && (
          <Message
            testid="accept-invite-error"
            icon={<XCircle size={28} className="text-[#C44]" />}
            title="Something went wrong"
            body="We couldn't process this invite right now. Please try again shortly."
          />
        )}
      </div>
    </div>
  );
}

function Message({ icon, title, body, testid, footerLink }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-start gap-3">
        <div className="mt-0.5">{icon}</div>
        <div>
          <div className="text-[15px] font-semibold text-black mb-1">
            {title}
          </div>
          <div className="text-[13px] text-[#555] leading-relaxed">{body}</div>
        </div>
      </div>
      {footerLink && (
        <Link
          to="/dashboard"
          className="mt-6 inline-flex h-10 items-center justify-center w-full rounded-md bg-[#1A4FFF] text-white text-[13px] font-semibold hover:bg-[#0e3fe6]"
          data-testid="accept-invite-dashboard-link"
        >
          Go to dashboard
        </Link>
      )}
    </div>
  );
}
